import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  FormControl,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import useAdd from '../../api/useAdd';
import useFetchObjects from '../../api/useFetchObjects';
import { useAuth } from '../../context/AuthContext';

const RoleCreate = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const token = user?.token;
  const navigate = useNavigate();

  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionIds: []
  });

  const [validationError, setValidationError] = useState('');
  
  // Fetch permissions from backend
  const { 
    data: permissions, 
    isLoading: permissionsLoading, 
    isError: permissionsError 
  } = useFetchObjects('permissions', 'permissions', token);

  // Add mutation hook
  const { 
    handleAdd, 
    loading: isSubmitting, 
    success
  } = useAdd('roles', token, '/roles', 
      t('roles.create_success', 'Role created successfully'),
      t('roles.create_error', 'Failed to create role')
  );

  // Group permissions by their type/category
  const [permissionGroups, setPermissionGroups] = useState([]);

  useEffect(() => {
    if (permissions && permissions.length > 0) {
      const groups = {};
      
      // Process permissions into groups by their code prefix
      permissions.forEach(permission => {
        // Split the code by underscore and take the first part as group
        const groupName = permission.code.split('_')[0];
        if (!groups[groupName]) {
          groups[groupName] = {
            name: groupName,
            label: t(`permissions.groups.${groupName}`, groupName),
            permissions: []
          };
        }
        groups[groupName].permissions.push(permission);
      });
      
      setPermissionGroups(Object.values(groups));
    }
  }, [permissions, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (e) => {
    const { name, checked } = e.target;
    const permissionId = name;

    setFormData(prev => {
      if (checked) {
        return {
          ...prev,
          permissionIds: [...prev.permissionIds, permissionId]
        };
      } else {
        return {
          ...prev,
          permissionIds: prev.permissionIds.filter(id => id !== permissionId)
        };
      }
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setValidationError(t('validation.role_name_required', 'Role name is required'));
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      handleAdd(formData);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          component={RouterLink}
          to="/roles"
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
        >
          {t('common.back', 'Back')}
        </Button>
        <Typography variant="h4" component="h1">
          {t('roles.create_title', 'Create New Role')}
        </Typography>
      </Box>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {permissionsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('permissions.fetch_error', 'Failed to load permissions')}
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('roles.basic_info', 'Basic Information')}
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                name="name"
                label={t('roles.name', 'Role Name')}
                placeholder={t('roles.name_placeholder', 'Enter role name')}
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label={t('roles.description', 'Description')}
                placeholder={t('roles.description_placeholder', 'Enter role description')}
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('roles.permissions', 'Permissions')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('roles.permissions_info', 'Select the permissions for this role')}
        </Typography>
        
        {permissionsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          permissionGroups.map((group) => (
            <Box key={group.name} sx={{ mb: 3 }}>
              <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {group.label}
                </FormLabel>
                <FormGroup>
                  <Grid container spacing={2}>
                    {group.permissions.map((permission) => (
                      <Grid item xs={12} sm={6} md={3} key={permission.id}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              name={permission.id}
                              checked={formData.permissionIds.includes(permission.id)}
                              onChange={handlePermissionChange}
                            />
                          }
                          label={permission.name}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </FormControl>
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          component={RouterLink}
          to="/roles"
          disabled={isSubmitting}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          variant="contained" 
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            t('common.save', 'Save')
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default RoleCreate; 